import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react'
import { Textarea, cn } from '@teach/ui'

interface Props {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  maxLength?: number
  className?: string
}

/**
 * Однострочное по смыслу поле, которое растёт на следующую строку, а не уезжает за край.
 * Enter и вставленные переводы строк не становятся текстом: это одна строка.
 */
export const AutoGrowTextField = forwardRef<HTMLTextAreaElement, Props>(
  ({ value, onChange, placeholder, maxLength, className }, forwarded) => {
    const fieldRef = useRef<HTMLTextAreaElement>(null)
    useImperativeHandle(forwarded, () => fieldRef.current as HTMLTextAreaElement)
    useLayoutEffect(() => {
      const field = fieldRef.current
      if (!field) return
      const fit = () => {
        field.style.height = 'auto'
        field.style.height = `${field.scrollHeight}px`
      }
      fit()
      // Шторка выезжает и ширина поля меняется без смены текста: число строк другое.
      if (typeof ResizeObserver === 'undefined') return
      // Высота — наша же правка наблюдаемого элемента: перемерять только при смене ширины,
      // иначе «ResizeObserver loop completed with undelivered notifications».
      let lastWidth = field.getBoundingClientRect().width
      const observer = new ResizeObserver((entries) => {
        const width = entries[entries.length - 1]?.contentRect.width ?? lastWidth
        if (width === lastWidth) return
        lastWidth = width
        fit()
      })
      observer.observe(field)
      return () => observer.disconnect()
    }, [value])
    return (
      <Textarea
        ref={fieldRef}
        rows={1}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\s*\n\s*/g, ' '))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault()
        }}
        maxLength={maxLength}
        placeholder={placeholder}
        className={cn('min-h-10 resize-none overflow-hidden rounded-xl', className)}
      />
    )
  },
)
AutoGrowTextField.displayName = 'AutoGrowTextField'
